import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import tenderAPI from '../../../services/tenderAPI';
import WorkspaceDataTable from '../../../components/workspace/WorkspaceDataTable';
import WorkspacePageHeader from '../../../components/workspace/WorkspacePageHeader';
import { useAdminRecordDelete } from '../../../components/workspace/useAdminRecordDelete';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { canEditTender } from '../../../utils/tenderRecordAccess';
import { formatWorkspaceDate } from '../../../utils/workspaceDates';
import { useWorkspaceAccess } from '../../../hooks/useWorkspaceAccess';
import { useMasterOptions } from '../../../components/workspace/MasterLookup';
import './TenderRegisterPage.scss';

const STAGE_LABELS = {
  identified: 'Identified',
  eligibility: 'Eligibility',
  bid_decision: 'Bid decision',
  preparing: 'In preparation',
  ready_to_submit: 'Ready to submit',
  submitted: 'Submitted',
  under_evaluation: 'Under evaluation',
  result_declared: 'Result declared',
  not_eligible: 'Not eligible',
  no_go: 'No-bid',
  won: 'Won',
  lost: 'Lost',
  not_qualified: 'Not qualified',
  cancelled: 'Cancelled',
  withdrawn: 'Withdrawn',
  assessing: 'Assessing',
  under_review: 'Under review',
  awarded: 'Awarded',
  identified_legacy: 'Identified',
  evaluating: 'Evaluating',
  pursuing: 'Pursuing'
};

const stageKey = (tender) => String(tender.workflowStage || tender.pipelineStage || tender.status || '').toLowerCase();

const formatDeadline = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return formatWorkspaceDate(date);
};

const isPastDeadline = (value) => {
  if (!value) return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
};

const TenderRegisterPage = () => {
  const navigate = useNavigate();
  const masters = useMasterOptions();
  const { allowed } = useWorkspaceAccess();
  const [loading, setLoading] = useState(true);
  const [tenders, setTenders] = useState([]);

  useDocumentTitle('Tender register');

  const load = useCallback(() => tenderAPI
    .getTenders()
    .then((res) => setTenders(res?.data?.tenders || res?.tenders || []))
    .finally(() => setLoading(false)), []);

  useEffect(() => {
    load();
  }, [load]);

  const admin = useAdminRecordDelete({
    entity: 'tender',
    noun: 'tender',
    plural: 'tenders',
    reload: load,
    label: (row) => row.reference || row.title
  });

  const tableRows = useMemo(
    () => tenders.map((tender) => {
      const key = stageKey(tender);
      const fromMaster = (masters.stages || []).find((stage) => stage.code === key)?.label;
      return {
        ...tender,
        stageKey: key,
        stageLabel: tender.lifecycle?.positionLabel || fromMaster || STAGE_LABELS[key] || key.replace(/_/g, ' ') || '—',
        deadlineLabel: formatDeadline(tender.deadline),
        deadlineOverdue: isPastDeadline(tender.deadline) && canEditTender(tender),
        serviceLabel: tender.serviceType || '—',
        clientLabel: tender.organization || ''
      };
    }),
    [tenders, masters.stages]
  );

  const columns = useMemo(
    () => [
      {
        key: 'reference',
        label: 'Reference',
        width: '148px',
        render: (value) => <span className="treg-ref">{value || '—'}</span>
      },
      {
        key: 'title',
        label: 'Opportunity',
        mobileTitle: true,
        render: (value, row) => (
          <div className="treg-title">
            <span className="treg-title__name">{value || '—'}</span>
            {row.clientLabel ? <span className="treg-title__client">{row.clientLabel}</span> : null}
          </div>
        )
      },
      {
        key: 'serviceLabel',
        label: 'Service',
        width: '180px',
        render: (value) => <span className="treg-service">{value}</span>
      },
      {
        key: 'stageLabel',
        label: 'Stage',
        width: '140px',
        render: (value, row) => (
          <span className={`treg-stage treg-stage--${row.stageKey || 'unknown'}`}>{value}</span>
        )
      },
      {
        key: 'deadlineLabel',
        label: 'Deadline',
        width: '140px',
        render: (value, row) => (
          <span className={`treg-date${row.deadlineOverdue ? ' treg-date--overdue' : ''}`}>{value}</span>
        )
      }
    ],
    []
  );

  const openRecord = (row, mode) => {
    const id = row._id || row.id;
    if (!id) return;
    if (mode === 'edit') {
      navigate(`/apps/tender/register/${id}/edit`, {
        state: { returnTo: '/apps/tender/register' }
      });
      return;
    }
    navigate(`/apps/tender/register/${id}`);
  };

  if (loading) return <Spinner animation="border" size="sm" />;

  return (
    <div className="tender-register">
      <WorkspacePageHeader
        title="Tender register"
        help="register"
        subtitle="Open any pursuit to review it. Edit stays available while the tender is in progress. Submitted, awarded, and lost records are view only."
        actions={allowed(['tender.create', 'tenders.create']) ? (
          <Button
            size="sm"
            variant="primary"
            onClick={() => navigate('/apps/tender/register/new', {
              state: { returnTo: '/apps/tender/register' }
            })}
          >
            New tender
          </Button>
        ) : null}
      />
      {admin.notice}
      <WorkspaceDataTable
        title="Tenders"
        downloadPermission="tenders.download"
        data={tableRows}
        columns={columns}
        searchPlaceholder="Search reference, title, service…"
        pageSize={12}
        onView={(row) => openRecord(row, 'view')}
        onEdit={(row) => openRecord(row, 'edit')}
        {...admin.tableProps}
        rowActions={admin.wrapActions((row) => (
          canEditTender(row)
            ? [
              { type: 'view', label: 'View' },
              { type: 'edit', label: 'Edit' }
            ]
            : [{ type: 'view', label: 'View' }]
        ))}
      />
      {admin.modal}
    </div>
  );
};

export default TenderRegisterPage;
